/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import moment from 'moment'

import {
  EuiDatePicker,
  EuiFieldText,
  EuiFormRow,
  EuiSelect,
  EuiSpacer,
  EuiRadioGroup,
  EuiPanel,
  EuiTitle,
  EuiText,
  EuiCallOut,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

/* ==========================================================================
   CONSTANTS & DOMAIN TYPES
   ========================================================================== */

const LEVEL_OPTIONS = [
  { value: 'standard', text: 'Standard' },
  { value: 'gold', text: 'Gold' },
  { value: 'platinum', text: 'Platinum' },
  { value: 'enterprise', text: 'Enterprise' },
]

export interface DirectState {
  start_date: string
  activation_code: string
  level: string
}

interface Props {
  values: DirectState
  touched: Partial<Record<keyof DirectState, boolean>>
  isDisabled: boolean
  onChange: (updates: Partial<DirectState>) => void
  onBlur: (field: keyof DirectState) => void
}

/**
 * Defines the two main paths for establishing a billing relationship.
 * CODE: The usage of an activation code
 * MANUAL: The kick of a 2 phased provisioning process
 */
enum ProvisionMode {
  CODE = 'code',
  MANUAL = 'manual',
}

/* ==========================================================================
   COMPONENT DEFINITION
   ========================================================================== */

const DirectProviderForm = ({ values, touched, isDisabled, onChange, onBlur }: Props) => {
  // --------------------------------------------------------------------------
  // State & Logic
  // --------------------------------------------------------------------------

  // We default to CODE mode to encourage the automated/standard workflow.
  const [mode, setMode] = useState<ProvisionMode>(ProvisionMode.CODE)

  /**
   * Handles switching between provisioning strategies.
   * CRITICAL: We aggressively clear data from the opposing mode to prevent
   * submitting "mixed" signals to the backend (e.g., sending both a manual date AND a code).
   */
  const handleModeChange = (newId: string) => {
    const newMode = newId as ProvisionMode
    setMode(newMode)

    if (newMode === ProvisionMode.CODE) {
      // Switched to Code -> Clear manual date
      onChange({ start_date: '' })
    } else {
      // Switched to Manual -> Clear code & level (derived from code)
      onChange({ activation_code: '' })
      onChange({ level: '' })
    }
  }

  const isInvalid = (field: keyof DirectState) => {
    const value = values[field]
    const normalizedValue = typeof value === 'string' ? value.trim() : value
    return touched[field] && !normalizedValue
  }
  // --------------------------------------------------------------------------
  // Render Helper: Activation Code Path
  // --------------------------------------------------------------------------

  const renderActivationFields = () => (
    <EuiPanel hasShadow={true} paddingSize='m'>
      <EuiTitle size='xxs'>
        <h3>
          <FormattedMessage id='direct.activation-code.header' defaultMessage='Order Binding' />
        </h3>
      </EuiTitle>

      <EuiText size='s' color='subdued'>
        <p>
          <FormattedMessage
            id='direct.activation_code.help_text'
            defaultMessage={
              'This attaches a pending order directly to this organization. ' +
              'The organization will converted to direct automatically once the deal start date is reached.'
            }
          />
        </p>
      </EuiText>

      <EuiSpacer size='m' />

      {/* Field: Activation Code */}
      <EuiFormRow
        label={<FormattedMessage id='direct.activation-code' defaultMessage='Activation Code' />}
        helpText={
          <FormattedMessage
            id='direct.activation_code.code.description'
            defaultMessage='The code generated during order provisioning.'
          />
        }
        isInvalid={isInvalid('activation_code')}
        error={
          isInvalid('activation_code') ? (
            <FormattedMessage
              id='direct.activation_code.code.description'
              defaultMessage='Activation code is required'
            />
          ) : undefined
        }
        fullWidth={true}
      >
        <EuiFieldText
          data-test-id='direct-activation_code-input'
          fullWidth={true}
          icon='tokenAlias'
          placeholder='e.g. 08ccf430-0461-4aea-9efc-6b1b0281b2d0'
          value={values.activation_code}
          onChange={(e) => onChange({ activation_code: e.target.value })}
          onBlur={() => onBlur('activation_code')}
          readOnly={isDisabled}
          isInvalid={isInvalid('activation_code')}
        />
      </EuiFormRow>

      <EuiSpacer size='m' />

      {/* Field: Subscription Level */}
      <EuiFormRow
        label={<FormattedMessage id='direct.level.label' defaultMessage='Subscription Level' />}
        helpText={
          <FormattedMessage
            id='direct.level.helpText'
            defaultMessage='The Subscription level that was negotiated for the new deal.'
          />
        }
        isInvalid={isInvalid('level')}
        fullWidth={true}
      >
        <EuiSelect
          data-test-id='direct-subscription_level-input'
          fullWidth={true}
          options={[{ value: '', text: 'Select level...' }, ...LEVEL_OPTIONS]}
          value={values.level}
          onChange={(e) => onChange({ level: e.target.value })}
          onBlur={() => onBlur('level')}
          disabled={isDisabled}
          isInvalid={isInvalid('level')}
        />
      </EuiFormRow>
    </EuiPanel>
  )

  // --------------------------------------------------------------------------
  // Render Helper: Manual Path
  // --------------------------------------------------------------------------

  const renderManualFields = () => (
    <EuiPanel hasShadow={true} paddingSize='m'>
      <React.Fragment>
        <EuiTitle size='xxs'>
          <h3>
            <FormattedMessage id='direct-conversion.header' defaultMessage='Conversion Signal' />
          </h3>
        </EuiTitle>

        <EuiText size='s' color='subdued'>
          <p>
            <FormattedMessage
              id='direct.activation_code.help_text'
              defaultMessage="This action sets the contract start date and enables credit provisioning for the 'Direct' domain."
            />
          </p>
        </EuiText>

        <EuiSpacer size='s' />

        {/* Warning Callout: Critical operational note for the user */}
        <EuiCallOut size='s' title='Two-Step Process' color='warning' iconType='alert'>
          <FormattedMessage
            id='direct.manual_provision.warning'
            defaultMessage={
              'This <strongText>does not grant credits</strongText>. ' +
              ' You must provision the prepaid(s) through CRM using this start date.'
            }
            values={{
              strongText: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </EuiCallOut>

        <EuiSpacer size='m' />

        {/* Field: Start Date */}
        <EuiFormRow
          data-test-id='direct-start-date-input'
          label={<FormattedMessage id='direct.start-date' defaultMessage='Contract Start Date' />}
          helpText='The effective date for the contract. This must match the prepaid when provisioned.'
          isInvalid={isInvalid('start_date')}
          fullWidth={true}
        >
          <EuiDatePicker
            fullWidth={true}
            placeholder='Select effective date'
            selected={values.start_date ? moment(values.start_date) : undefined}
            onChange={(date) => onChange({ start_date: date ? date.format('YYYY-MM-DD') : '' })}
            onBlur={() => onBlur('start_date')}
            disabled={isDisabled}
            isInvalid={isInvalid('start_date')}
          />
        </EuiFormRow>
      </React.Fragment>
    </EuiPanel>
  )

  // --------------------------------------------------------------------------
  // Main Render Layout
  // --------------------------------------------------------------------------

  const options = [
    {
      id: ProvisionMode.CODE,
      label: 'Activation Code',
      disabled: isDisabled,
      'data-test-id': 'direct-activation_code-selector',
    },
    {
      id: ProvisionMode.MANUAL,
      label: 'Manual',
      disabled: isDisabled,
      'data-test-id': 'direct-manual-method-selector',
    },
  ]
  return (
    <React.Fragment>
      {/* Mode Switcher */}
      <EuiFormRow
        label='Provisioning Method'
        helpText='Select the method for establishing the Direct billing agreement.'
      >
        <EuiRadioGroup
          options={options}
          idSelected={mode}
          onChange={handleModeChange}
          name='provision-mode'
        />
      </EuiFormRow>

      <EuiSpacer size='m' />

      {/* Dynamic Form Content */}
      <EuiPanel color='subdued' hasShadow={false} paddingSize='m'>
        {mode === ProvisionMode.CODE ? renderActivationFields() : renderManualFields()}
      </EuiPanel>
    </React.Fragment>
  )
}

export default DirectProviderForm
